import { EMPTY, from, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticsService, DynamicTranslationService } from '@gs-2.0/core';
import { loadStripe, Stripe } from '@stripe/stripe-js';

import { environment } from '@environment';
import { AuthService } from '../../../_core/api/auth.service';
import { PaymentService } from '../../../_core/api/payment.service';
import { TransactionService } from '../../../_core/api/transaction.service';
import { CONTRIBUTION_TYPE } from '../../../_core/constants/Contribute';
import FundTypes from '../../../_core/constants/fund-types';
import PageTitles from '../../../_core/constants/page-titles';
import { PaymentStatus } from '../../../_core/constants/Payment';
import { SERVER_ERROR } from '../../../_core/constants/ServerError';
import Urls from '../../../_core/constants/urls';
import { Charity } from '../../../_core/models/charity.model';
import { CurrencySet } from '../../../_core/models/contributeData';
import { CustomControl } from '../../../_core/models/CustomForms.model';
import { PaymentResponseDto, PaymentSessionResponse } from '../../../_core/models/PaymentResponse';
import { TitleService } from '../../../_core/services/title.service';
import { PortalToastService } from '../../../_shared/services/portal-toast.service';
import { NETWORK_MEMBERS_WITH_PORTAL, RedirectTargets } from './donate-form.const';
import {
  CheckContributionPayload,
  CodeName,
  ContributionConstData,
  ContributionDynamicData,
  CurrencyCheck,
  CustomLinkFund,
  GetNextActionPayload,
  NextActionResponse,
  PaymentPayload,
  RepopulateData,
  SearchCountry,
  TokenData,
} from './donate-form.model';

@Injectable()
export class DonateAsyncService {
  private showErrorToast(translationKey: string, params?: any, sticky = false): string {
    const message = params
      ? this.translateService.instant(translationKey, params)
      : this.translateService.instant(translationKey);
    this.portalToastService.error(message, null ,sticky);
    this.dynamicData.loading = false;
    return message;
  }

  handleBasicError = (error: any) => {
    this.showErrorToast('error.basic_error', { homepage: environment.fund.homeLink });
    return throwError(() => error);
  };

  /**
   * Fires form_error for the public donation funnel (GA4), reusing the
   * user-facing message already resolved for the toast. No-ops for the
   * private/authenticated flow.
   */
  private trackFormError(formStep: number, errorField: string | undefined, errorMessage: string): void {
    if (!this.constData?.isPublic) return;
    this.analyticsService.trackFormError('donation', formStep, errorField, 'donation_form', errorMessage);
  }

  /** Tracks a form_error using the same translation-key/literal resolution `showToastFromErrorMessage` applies, without showing a toast. */
  private trackResolvedFormError(formStep: number, errorField: string | undefined, rawMessage: string): void {
    const resolved = this.isTranslationKey(rawMessage)
      ? this.translateService.instant(rawMessage.replace(/_/g, '.'))
      : rawMessage;
    this.trackFormError(formStep, errorField, resolved);
  }

  /** Shows the generic basic-error toast, then tracks form_error for the given step. */
  private trackBasicFormError(formStep: number): void {
    const message = this.translateService.instant('error.basic_error', { homepage: environment.fund.homeLink });
    this.trackFormError(formStep, undefined, message);
  }

  constData: ContributionConstData;
  dynamicData: ContributionDynamicData;
  charityInfoLoaded = false;

  // prettier ignore
  constructor(
    private transactionService: TransactionService,
    private paymentService: PaymentService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private translateService: DynamicTranslationService,
    private titleService: TitleService,
    private portalToastService: PortalToastService,
    private analyticsService: AnalyticsService
  ) {}

  initContributionProps(constData: ContributionConstData, dynamicData: ContributionDynamicData) {
    this.constData = constData;
    this.dynamicData = dynamicData;
  }

  private sortCountriesWithPriority(countries: any[]): any[] {
    const priorityNames = ['Switzerland', 'United States', 'United Kingdom', 'Australia'];
    const priority = priorityNames
      .map(name => countries.find(c => c.name === name))
      .filter(Boolean);
    const rest = countries
      .filter(c => !priorityNames.includes(c.name))
      .sort((a, b) => a.name.localeCompare(b.name));
    return [...priority, ...rest];
  }

  getAllCountries(): Observable<any> {
    this.dynamicData.loading = true;
    return this.transactionService.getCountriesByString('').pipe(
      tap((results: any) => {
        const sorted = this.sortCountriesWithPriority(results.array);
        this.constData.availableCountries = sorted;
        this.constData.countries = sorted;
        if (this.constData.giverInfo) {
          this.constData.giverInfo.country = this.constData.countries.find(
            (country: CodeName) => country.name === this.constData.giverInfo.country?.name
          );
        }
        this.dynamicData.loading = false;
      }),
      catchError((error) => {
        this.trackBasicFormError(1);
        return this.handleBasicError(error);
      })
    );
  }

  getApplicationAssesmentFund(): Observable<any> {
    this.dynamicData.loading = true;
    return this.transactionService.getApplicationAssesmentFund().pipe(
      map((response: CustomLinkFund) => {
        response.name = response.vendorName;
        return response;
      }),
      catchError((error) => {
        this.trackBasicFormError(1);
        return this.handleBasicError(error);
      })
    );
  }

  searchCharityByName(
    searchText: string,
    countryName: string,
    isTaxDeductible: boolean,
    control: CustomControl
  ): Observable<any> {
    control.loading = true;
    const payload = {
      charityNameOrNumber: searchText || '',
      countryName: this.constData.isSwiss ? countryName || 'country' : 'other',
      taxDeduction: this.constData.isSwiss || this.constData.isTol ? isTaxDeductible : false,
    };
    return this.transactionService.getCharityByString(payload).pipe(
      tap((results: any) => {
        this.dynamicData.charitySearchResults = results.map((charity: any) => new Charity(charity));
        control.loading = false;
      }),
      catchError((error) => {
        console.error(error);
        const message = this.showErrorToast('error.basic_error', { homepage: environment.fund.homeLink });
        this.trackFormError(1, 'charity', message);
        control.loading = false;
        return throwError(() => error);
      })
    );
  }

  determineContributionStatus(
    payload: GetNextActionPayload,
    countryDonatingFrom?: SearchCountry
  ): Observable<any> {
    this.dynamicData.loading = true;
    return this.transactionService.getNextAction(payload).pipe(
      map((response: NextActionResponse) => {
        const nextAction = { ...response };
        const targetEnvironment = RedirectTargets.find(
          (environment) => environment.tenantId === response.redirect
        );
        // A withPortal() country (Germany/US/Canada) now carries its own network member's tenant base URL
        // on contriConfig.redirectUrl, resolved from the country/network-member data rather than the
        // static per-environment table above -- that table stays the fallback for everyone else.
        // redirectUrl is also read by confirmRedirect() (as networkMemberRedirectUrl) and by
        // post-payment.component.ts for the post-payment "return to our site" link -- an admin configures
        // it via contriConfig.childTenantUrl, which the configurator mirrors into redirectUrl, so this is
        // the one field the runtime is meant to read (see childTenantUrl's doc comment in the model).
        const networkMemberRedirectUrl = countryDonatingFrom?.networkMember?.contriConfig?.redirectUrl;
        const isWithPortalCountry = NETWORK_MEMBERS_WITH_PORTAL.includes(countryDonatingFrom?.name);
        if (isWithPortalCountry && networkMemberRedirectUrl) {
          nextAction.redirect = networkMemberRedirectUrl;
        } else if (targetEnvironment) {
          nextAction.redirect = targetEnvironment.environmentUrl;
        }
        if (!this.dynamicData.swissContributionInfo) {
          this.dynamicData.swissContributionInfo = nextAction;
          this.dynamicData.setNextButtonText(
            payload.contributionType,
            payload.taxDeduction,
            this.constData.isSwiss,
            this.constData.isCncf
          );
          if (this.dynamicData.willRedirect)
            this.dynamicData.redirectTarget = targetEnvironment?.name;
        } else if (!this.dynamicData.willRedirect)
          this.showErrorToast('error.basic_error', { homepage: environment.fund.homeLink });
        this.dynamicData.loading = false;
        return;
      }),
      catchError((error) => {
        console.error(error);
        this.dynamicData.loading = false;
        let message: string;
        switch (error.error.message) {
          case SERVER_ERROR.INVALID_CURRENCY:
            message = this.showErrorToast('error.invalid_currency', { homepage: environment.fund.homeLink });
            break;
          case SERVER_ERROR.INVALID_COUNTRY:
            message = this.showErrorToast('error.invalid_country', { homepage: environment.fund.homeLink });
            break;
          case SERVER_ERROR.BANK_TRANSFER_INSTRUCTION_NOT_FOUND:
            message = this.showErrorToast('error.bank_instruction_not_found', {
              homepage: environment.fund.homeLink,
            });
            break;
          case SERVER_ERROR.DAF_NOT_FOUND:
            const translatedMsg = this.translateService.instant('error.daf_not_found', {
              networkMember: error.error?.data?.networkMember,
              adminEmail: error.error?.data?.adminEmail,
            });
            this.portalToastService.error(translatedMsg);
            message = translatedMsg;
            break;
          default:
            message = this.showErrorToast('error.basic_error', { homepage: environment.fund.homeLink });
            break;
        }
        this.trackFormError(1, undefined, message);
        return throwError(() => error);
      })
    );
  }

  getRedirectInfo(): Observable<TokenData> {
    this.dynamicData.loading = true;
    return this.transactionService.repopulate(this.constData.token).pipe(
      map(([data, currencies]: [RepopulateData, CurrencySet[]]) => {
        currencies = currencies.map((currency: any) => {
          currency.uuid = currency.id;
          return currency;
        });
        this.constData.purchaseContributionType = data.purchaseContributionType;
        this.dynamicData.loading = false;
        return { data, currencies };
      }),
      catchError((error) => {
        this.trackBasicFormError(1);
        return this.handleBasicError(error);
      })
    );
  }

  getCountryDetails(countryUuid: string, countryControl: CustomControl): Observable<SearchCountry> {
    // Validate countryUuid exists
    if (!countryUuid) {
      console.warn('getCountryDetails called with null/undefined countryUuid');
      countryControl.loading = false;
      return EMPTY; // Return empty observable instead of throwing error
    }

    countryControl.loading = true;
    return this.transactionService.getCountryDetails(countryUuid).pipe(
      tap(() => (countryControl.loading = false)),
      catchError((error) => {
        const message = this.showErrorToast('error.basic_error', { homepage: environment.fund.homeLink });
        this.trackFormError(1, 'countryDonatingFrom', message);
        countryControl.loading = false;
        return throwError(() => error);
      })
    );
  }

  checkContribution(
    charityPayload: CheckContributionPayload,
    charityControl: CustomControl,
    networkMemberInfo = false
  ): Observable<any> {
    charityControl.loading = true;
    this.dynamicData.isNetworkMemberInfoLoading = networkMemberInfo;
    return this.transactionService.checkContribution(charityPayload).pipe(
      tap((response) => {
        if (response.message) this.dynamicData.setWarningMessage(response.message);
        this.dynamicData.isNetworkMemberInfoLoading = false;
        charityControl.loading = false;
      }),
      catchError((error) => {
        console.error(error);
        const message = this.showErrorToast('error.basic_error', { homepage: environment.fund.homeLink });
        this.trackFormError(1, 'charity', message);
        charityControl.loading = false;
        return throwError(() => error);
      })
    );
  }

  getCharityInfo(): Observable<CustomLinkFund> {
    this.dynamicData.loading = true;
    return this.transactionService.getFundNumberFromHash(this.constData.customLink).pipe(
      map((response: CustomLinkFund) => {
        if (!(response.vendorName || response.status))
          throw new Error(SERVER_ERROR.FUND_NUMBER_NOT_FOUND);
        response.name = response.vendorName;
        this.dynamicData.loading = false;
        return response;
      }),
      catchError((err) => {
        let message: string;
        switch (err.error ? err.error?.message : err.message) {
          case SERVER_ERROR.EXPIRED_FUND:
            const expiredMsg = this.setDonateErrorMessage(err.error.description);
            message = this.showErrorToast(expiredMsg, { homepage: environment.fund.homeLink }, true);
            break;
          case SERVER_ERROR.FUND_NUMBER_NOT_FOUND:
            message = this.showErrorToast('error.fund_not_found', { homepage: environment.fund.homeLink });
            break;
          case SERVER_ERROR.SUSPENDED_FUND:
            const suspendedMsg = this.setDonateErrorMessage(err.error.description);
            message = this.showErrorToast(suspendedMsg, { homepage: environment.fund.homeLink }, true);
            break;
          default:
            message = this.showErrorToast('error.basic_error', { homepage: environment.fund.homeLink });
        }
        this.trackFormError(1, 'charity', message);
        this.dynamicData.loading = false;
        this.dynamicData.canDonate = false;
        return throwError(() => err);
      }),
      finalize(() => (this.charityInfoLoaded = true))
    );
  }

  setDonateErrorMessage(
    fundType: string,
    responseType: { error: boolean } = { error: true }
  ): string {
    const type = fundType.toLowerCase() as FundTypes;
    let msg = '';

    switch (type) {
      case FundTypes.CHARITY:
        msg = 'error.cannot_donate_charity';
        responseType.error === true &&
          this.titleService.titleChanged(PageTitles.CONTRIBUTION_PUBLIC);
        break;
      case FundTypes.PROJECT:
        msg = 'error.cannot_donate_project';
        this.titleService.titleChanged(PageTitles.CONTRIBUTION_PUBLIC_PROJECT);
        break;
      default:
        msg = 'error.cannot_donate_fund';
        break;
    }

    return msg;
  }

  getAllCurrencies(): Observable<any> {
    const getPrefillData = (currencies: CurrencySet[]) => {
      if (this.constData.isCncf) {
        this.dynamicData.availableCurrencies = currencies.filter(
          (currency: CurrencySet) => currency.symbol === 'CAD'
        );
        return { currencyToPrefill: this.dynamicData.availableCurrencies[0], disable: true };
      } else if (this.constData.isIgf) {
        this.dynamicData.availableCurrencies = currencies.filter(
          (currency: CurrencySet) => currency.symbol === 'USD'
        );
        return { currencyToPrefill: this.dynamicData.availableCurrencies[0], disable: true };
      } else {
        this.dynamicData.availableCurrencies = currencies;
        if (this.dynamicData.availableCurrencies.length === 1)
          return { currencyToPrefill: this.dynamicData.availableCurrencies[0], disable: true };
      }
      return {};
    };
    if (this.constData.isPublic) {
      this.dynamicData.loading = true;
      return this.authService.getCurrencies().pipe(
        map((currencies: CurrencySet[]) => {
          if (this.charityInfoLoaded) this.dynamicData.loading = false;
          currencies.forEach((currency: any) => (currency.uuid = currency.id));
          return getPrefillData(currencies);
        }),
        catchError((error) => {
          this.trackBasicFormError(1);
          return this.handleBasicError(error);
        })
      );
    } else return of(getPrefillData(this.constData.userCurrencies));
  }

  initiatePlaid(
    charity: any,
    formData: any,
    isRecurring = false,
    homeLink: string,
    redirectCustomLink: string,
    gsUrl: string
  ): Observable<any> {
    return this.paymentService.getLinkToken().pipe(
      map((response) => {
        const contribution = { charity, formData };
        const plaidConfigs = {
          token: response,
          onSuccess: (public_token: string, metadata: any) =>
            this.proceedToPlaidPayment(
              public_token,
              metadata,
              contribution,
              isRecurring,
              homeLink,
              redirectCustomLink,
              gsUrl
            ),
        };
        return plaidConfigs;
      }),
      catchError((error) => {
        this.trackBasicFormError(2);
        return this.handleBasicError(error);
      })
    );
  }

  proceedToPlaidPayment(
    token: string,
    metadata: any,
    contribution: any,
    isRecurring = false,
    homeLink: string,
    redirectCustomLink = '',
    gsUrl = ''
  ): void {
    this.dynamicData.loading = true;

    const accountId = metadata.accounts[0].id;
    const payload = new PaymentPayload(
      contribution.formData,
      contribution.charity,
      this.constData,
      token,
      accountId
    );
    const addRecurringPayload = {
      ...payload,
      recurring: isRecurring,
      startDate:
        contribution.formData.financialInfo?.recurrenceForm?.recurrenceRange?.startDate || null,
      endDate:
        contribution.formData.financialInfo?.recurrenceForm?.recurrenceRange?.endDate || null,
      recurrencePattern:
        contribution.formData.financialInfo?.recurrenceForm?.recurrencePattern || null,
      charity: isRecurring ? contribution.charity.name : null,
    };
    let redirectLink = this.constData.redirectReturnLink
      ? { redirectLink: this.constData.redirectReturnLink }
      : {};

    this.paymentService.exchangeToken(addRecurringPayload).subscribe(
      (response: PaymentResponseDto) => {
        const queryParams = {
          ...response,
          redirectLink: response.link || redirectLink,
          contributionType: contribution.formData.contributionTypeInfo.contributionType.code,
          recurrencePattern: addRecurringPayload.recurrencePattern,
          customLink: this.constData.customLink || redirectCustomLink || null,
          homeLink,
          gsUrl,
          utmSource: this.constData.utmSource || null,
          signature: this.constData.signature || null,
        };

        !gsUrl && delete queryParams.gsUrl;
        delete queryParams.link;

        this.router.navigate([Urls.CONFIRM], {
          relativeTo: this.route,
          queryParams,
        });
      },
      (error) => {
        const getErrorMessage = (): string => {
          switch (error.error?.message) {
            case SERVER_ERROR.INVALID_CURRENCY:
              return 'error.invalid_currency';
            case SERVER_ERROR.INVALID_COUNTRY:
              return 'error.invalid_country';
            default:
              return 'SUMMARY_STATUS_FAILED';
          }
        };
        const errorMessage = getErrorMessage();
        this.trackResolvedFormError(2, undefined, errorMessage);
        this.router.navigate([Urls.CANCEL], {
          relativeTo: this.route,
          queryParams: {
            ...redirectLink,
            status: PaymentStatus.FAILED,
            error: errorMessage,
            customLink: this.constData.customLink || null,
            utmSource: this.constData.utmSource || null,
            signature: this.constData.signature || null,
          },
        });
        this.dynamicData.loading = false;
      }
    );
  }

  initiateStripe(
    applicationAssessment: boolean,
    payload: any,
    isRecurring = false
  ): Observable<any> {
    this.dynamicData.loading = true;
    let sessionId: string;
    return this.paymentService.getStripeSessionId(applicationAssessment, payload, isRecurring).pipe(
      map((response: PaymentSessionResponse) => response.stripeSessionId),
      switchMap((stripeSessionId: string) => {
        sessionId = stripeSessionId;
        const stripeKey = this.dynamicData.swissContributionInfo?.stripePublicKey || environment.fund.stripeKey;
        return from(loadStripe(stripeKey)).pipe(
          switchMap((stripe: Stripe) =>
            from(stripe.redirectToCheckout({ sessionId })).pipe(
              tap(this.handleBasicError),
              catchError(this.handleBasicError)
            )
          )
        );
      }),
      catchError((error) => {
        const getErrorMessage = (): string => {
          switch (error.error?.message) {
            case SERVER_ERROR.INVALID_CURRENCY:
              return 'error.invalid_currency';
            case SERVER_ERROR.INVALID_COUNTRY:
              return 'error.invalid_country';
            case SERVER_ERROR.NOT_PERMITTED_STRIPE_CURRENCY:
              return this.translateService.instant('error.currency_mismatch_stripe', {
                currency: error.error?.data?.currency,
              });
            case SERVER_ERROR.NOT_PERMITTED_STRIPE:
              return this.translateService.instant('error.currency_mismatch', {
                currency: this._getLastWord(error.error?.description),
              });
            case SERVER_ERROR.NOT_PERMITTED_START_DATE:
              return 'error.start_date_error';
            case SERVER_ERROR.NOT_PERMITTED_END_DATE:
              return 'error.end_date_error';
            default:
              return 'summary_status_failed';
          }
        };
        const message = this.showToastFromErrorMessage(getErrorMessage());
        this.trackFormError(2, undefined, message);
        return EMPTY;
      })
    );
  }

  submitOfflinePayment(
    payload: PaymentPayload,
    contributionType: CONTRIBUTION_TYPE,
    countryDetails: SearchCountry,
    taxDeduction: boolean,
    homeLink: string,
    redirectCustomLink = '',
    gsUrl = '',
    isPayOffline?: boolean
  ): void {
    const redirectLink = this.constData.redirectReturnLink
      ? { redirectLink: this.constData.redirectReturnLink }
      : {};
    const recurrencePattern = payload.recurrencePattern;
    this.paymentService.submitOfflinePayment(payload).subscribe(
      (response: PaymentResponseDto) => {
        this.router.navigate([Urls.CONFIRM], {
          relativeTo: this.route,
          queryParams: {
            contributionType,
            amount: response.amount,
            currencySymbol: response.currencySymbol,
            countryName: countryDetails?.name,
            countryUuid: countryDetails?.uuid,
            networkMemberLogo: countryDetails?.networkMember?.logoUrl,
            status: PaymentStatus.PENDING,
            taxDeduction,
            entityName: response.entityName,
            cseg5: response.cseg5,
            fundNumber: payload?.fundNumberDigits,
            recurrencePattern: recurrencePattern,
            ...redirectLink,
            customLink: this.constData.customLink || redirectCustomLink || null,
            homeLink,
            country: this.dynamicData.swissContributionInfo?.text1 || countryDetails?.name,
            networkMemberName:
              this.dynamicData.swissContributionInfo?.text2 || countryDetails?.networkMember?.name,
            gsUrl,
            utmSource: this.constData.utmSource || null,
            signature: this.constData.signature || null,
            isPayOffline: isPayOffline,
          },
        });
      },
      (error) => {
        const getErrorMessage = (): string => {
          switch (error.error?.message) {
            case SERVER_ERROR.INVALID_CURRENCY:
              return 'error.invalid_currency';
            case SERVER_ERROR.INVALID_COUNTRY:
              return 'error.invalid_country';
            default:
              return 'SUMMARY_STATUS_FAILED';
          }
        };
        const errorMessage = getErrorMessage();
        this.trackResolvedFormError(2, undefined, errorMessage);
        this.router.navigate([Urls.CANCEL], {
          relativeTo: this.route,
          queryParams: {
            ...redirectLink,
            status: PaymentStatus.FAILED,
            error: errorMessage,
            customLink: this.constData.customLink || null,
            utmSource: this.constData.utmSource || null,
            signature: this.constData.signature || null,
          },
        });
      }
    );
  }

  getMinimumAmountForCurrency(
    control: CustomControl,
    currencySymbol: string
  ): Observable<CurrencyCheck> {
    control.loading = true;
    return this.transactionService.checkMinimumAmount(currencySymbol).pipe(
      tap(() => (control.loading = false)),
      catchError((error) => {
        console.error(error);
        const message = this.showErrorToast('error.basic_error', { homepage: environment.fund.homeLink });
        this.trackFormError(1, 'amount', message);
        control.loading = false;
        return throwError(() => error);
      })
    );
  }

  private isTranslationKey(message: string): boolean {
    return !message.includes(' ');
  }

  private showToastFromErrorMessage(errorMessage: string): string {
    if (this.isTranslationKey(errorMessage)) {
      const key = errorMessage.replace(/_/g, '.');
      return this.showErrorToast(key);
    } else {
      this.portalToastService.error(errorMessage);
      this.dynamicData.loading = false;
      return errorMessage;
    }
  }

  private _getLastWord(sentence: string) {
    const words = sentence.split(' ');
    const lastWord = words[words.length - 1];
    return lastWord.replace(/\.$/, '').toUpperCase(); // Remove the trailing period and convert to uppercase
  }
}
